import {
  Alert,
  Card,
  RadialProgress,
  RateBars,
  ScrollRow,
  StatTile,
  StatusPill,
  cn,
} from "@breenova/ui";
import type { components } from "@breenova/api-client";
import {
  ArrowRight,
  Baby,
  Bird as BirdIcon,
  Egg as EggIcon,
  ChevronDown,
  Heart,
  Plus,
  Search,
  Syringe,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { useAuth } from "@/auth/auth-context";
import { PageHeader, RailGroup, RailRow, RailSection } from "@/components/app-shell";
import {
  incubationProgress,
  seasonLabel,
  useActiveNests,
  useBreedingCalendar,
  type CalendarEntry,
  type NestSummary,
} from "@/lib/breeding";
import { useHealthRecords, type HealthRecordDto } from "@/lib/health";
import { useOverview } from "@/lib/stats";

type SeasonRatesData = components["schemas"]["RatesDto2"];

/**
 * Today: the two or three things that need the breeder now, then the aviary at a glance.
 *
 * Ordered by what is actionable, not by what is impressive. The counts sit under the
 * task list on a phone and beside it on a desktop, because a total of 248 birds is never
 * the reason someone opens this screen at six in the morning — an overdue ringing is.
 */
export function DashboardPage() {
  const { user } = useAuth();

  const overview = useOverview();
  const nests = useActiveNests();
  const calendar = useBreedingCalendar();
  const health = useHealthRecords("Active");

  const today = new Date().toISOString().slice(0, 10);
  const entries = calendar.data?.entries ?? [];

  // Overdue and today. Those are the two the breeder has to be present for; the rest is
  // a forecast and belongs on the calendar.
  const due = useMemo(
    () => groupTasks(entries.filter((entry) => entry.daysAway <= 0)),
    [entries],
  );

  const stats = overview.data;
  const running = nests.data ?? [];
  const eggsInNest = running.reduce((total, nest) => total + nest.eggCount, 0);
  const fertileInNest = running.reduce((total, nest) => total + nest.fertileCount, 0);

  /*
   * The one live withdrawal, if there is one.
   *
   * First rather than a list: a withdrawal is a prohibition, and the aside's job is to
   * say one is running at all. Which birds and for how long is the Care screen, and the
   * card links there.
   */
  const withdrawal = (health.data?.items ?? []).find((record) => record.isWithdrawalActive);

  const cages = useCageRollup(running, entries);

  return (
    <div className="flex flex-col md:gap-xl">
      <RailSection>
        {cages.length > 0 ? (
          <RailGroup label="Cages with work">
            {cages.map((cage) => (
              <RailRow
                key={cage.code}
                to="/breeding"
                label={cage.code}
                tone={cage.late > 0 ? "alert" : "default"}
                trailing={
                  cage.late > 0
                    ? `${cage.late} late`
                    : `${cage.nests} ${cage.nests === 1 ? "nest" : "nests"}`
                }
                leading={
                  <span
                    aria-hidden
                    className={cn(
                      "size-2.25 shrink-0 rounded-full",
                      cage.late > 0 ? "bg-action" : "bg-primary",
                    )}
                  />
                }
              />
            ))}
            <RailRow
              to="/more/locations"
              label="New cage"
              leading={<Plus className="size-4 shrink-0" aria-hidden />}
            />
          </RailGroup>
        ) : null}
      </RailSection>

      <PageHeader
        kicker={longDate(today)}
        title={`${greeting()}, ${firstName(user?.displayName)}`}
        action={
          /* The phone's search is a round button into the register, which already
             searches ring numbers, cages and mutations; the desktop gets the field. */
          <Link
            to="/birds"
            aria-label="Search the register"
            className={cn(
              "flex size-11.5 items-center justify-center rounded-full md:hidden",
              "bg-surface-container-lowest text-on-surface shadow-card",
            )}
          >
            <Search className="size-5" aria-hidden />
          </Link>
        }
        toolbar={
          <>
            <TodaySearch />

            {/* The season this screen is counting. It reads as a chooser because that is
                what it is — the seasons screen is where a year is opened and made
                current, and this is the shortest way to it. */}
            <Link
              to="/breeding/seasons"
              className={cn(
                "hidden h-12 shrink-0 items-center gap-2 rounded-control px-lg md:inline-flex",
                "bg-surface-container-lowest text-pill text-on-surface shadow-card",
                "transition-colors hover:bg-surface-container-low",
              )}
            >
              <span data-numeric>
                {stats?.currentSeasonYear
                  ? `Season ${seasonLabel({
                      year: stats.currentSeasonYear,
                      name: stats.currentSeasonName,
                    })}`
                  : "No season open"}
              </span>
              <ChevronDown className="size-4 text-on-surface-variant" aria-hidden />
            </Link>
          </>
        }
      />

      <div className="flex flex-col gap-5.5 px-5 md:gap-lg md:px-gutter-wide">
        {overview.isError ? (
          <Alert tone="error">
            Could not reach the API. Start it with <code>dotnet run</code>, then{" "}
            <button type="button" className="underline" onClick={() => void overview.refetch()}>
              try again
            </button>
            .
          </Alert>
        ) : null}

        <div className="flex flex-col gap-5.5 lg:flex-row lg:items-start lg:gap-6">
          {/*
            Two independent decks from `lg`, and independent is the point: as a grid the
            aside spanning two rows set the height of both, which pushed the running
            nests away from the task list they belong under. `display:contents` on a
            phone dissolves both wrappers so the six blocks become one column again, and
            `order` puts the counts back between the two lists where they were argued to
            belong.
          */}
          <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-6">
            <div className="order-1">
              <NeedsYouToday tasks={due} pending={calendar.isPending} />
            </div>

            <div className="order-3">
              <NestsRunning nests={running} pending={nests.isPending} today={today} />
            </div>
          </div>

          <div className="contents lg:flex lg:w-aside-today lg:shrink-0 lg:flex-col lg:gap-lg">
          <section className="order-2 flex flex-col gap-3 lg:gap-lg">
            <div className="flex items-baseline justify-between gap-sm">
              <h2 className="text-section-header text-on-surface lg:text-section-header-wide">
                The aviary
              </h2>
              <span className="text-kicker uppercase text-primary-strong" data-numeric>
                {stats?.currentSeasonYear
                  ? `Season ${seasonLabel({
                      year: stats.currentSeasonYear,
                      name: stats.currentSeasonName,
                    })}`
                  : "No season open"}
              </span>
            </div>

            {/* Four across the phone's width, two by two in the desktop rail — the tiles
                are square-ish either way rather than four slivers in a 380px column. */}
            <div className="grid grid-cols-4 gap-2 lg:grid-cols-2 lg:gap-3">
              <StatTile
                label="Birds"
                value={format(stats?.totalBirds)}
                icon={<BirdIcon className="size-4.25 lg:size-4.5" />}
                hint={
                  stats ? `${stats.males} cocks · ${stats.females} hens` : undefined
                }
              />
              <StatTile
                label="Pairs"
                value={format(stats?.activePairs)}
                icon={<Heart className="size-4.25 lg:size-4.5" />}
                hint={
                  stats
                    ? `${stats.activeNests} ${stats.activeNests === 1 ? "round" : "rounds"} running`
                    : undefined
                }
              />
              <StatTile
                label="Eggs"
                value={nests.isPending ? "—" : format(eggsInNest)}
                icon={<EggIcon className="size-4.25 lg:size-4.5" />}
                hint={
                  nests.isPending
                    ? undefined
                    : `${fertileInNest} fertile · ${eggsInNest - fertileInNest} to candle`
                }
              />
              <StatTile
                label="Ringed"
                value={format(stats?.currentSeason?.chicksRinged)}
                icon={<Baby className="size-4.25 lg:size-4.5" />}
                hint={
                  stats
                    ? `${stats.chicksInNest} still in the nest`
                    : undefined
                }
              />
            </div>

            <SeasonRates rates={stats?.currentSeason} />

            {withdrawal ? <WithdrawalCard record={withdrawal} today={today} /> : null}
          </section>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * How the season is going, on the desktop only.
 *
 * Deliberately absent from the phone. It is the same argument the task list already
 * makes by showing two rows there and five here: the amount shown follows the room
 * there is to show it in, and the aside on a wide screen has room the phone does not.
 * Nothing is fetched for it — the overview already carries the season's rates, and the
 * dashboard was using one number out of eleven.
 *
 * The bars are the ones a breeder acts on. Nest mortality is the fourth on the
 * statistics screen and stays there: it is a number to study, not to glance at over
 * a cup of tea.
 */
function SeasonRates({ rates }: { rates: SeasonRatesData | undefined }) {
  if (!rates) {
    return null;
  }

  return (
    <Card tone="recessed" flat className="hidden flex-col gap-3 p-lg lg:flex">
      <div className="flex items-baseline justify-between gap-sm">
        <span className="text-label-caps uppercase text-on-surface-body">This season</span>
        <Link to="/statistics" className="text-label-caps uppercase text-on-primary-fixed-variant">
          Statistics
        </Link>
      </div>

      <RateBars
        bars={[
          { label: "Fertility", value: rates.fertilityRate },
          { label: "Hatch", value: rates.hatchRate },
          { label: "Weaning", value: rates.weaningRate },
        ]}
        layout="inline"
      />
    </Card>
  );
}

/**
 * The hero: what needs the breeder today, and the first few of them.
 *
 * Capped at three rows on a phone. A fourth pushes the aviary counts off a 390px screen,
 * and a task list that scrolls is one a breeder stops reading at the top.
 */
/** Rows the hero shows before it defers to the calendar. */
const VISIBLE_TASKS_NARROW = 3;
const VISIBLE_TASKS_WIDE = 5;

function NeedsYouToday({
  tasks,
  pending,
}: {
  tasks: readonly GroupedTask[];
  pending: boolean;
}) {
  /* Counted in birds, not in rows: one collapsed row standing for three late chicks is
     three things past their window, and the figure at the top has to say three. */
  const total = tasks.reduce((sum, task) => sum + task.count, 0);
  const late = tasks
    .filter((task) => task.entry.isOverdue)
    .reduce((sum, task) => sum + task.count, 0);

  if (pending) {
    return (
      <Card tone="accent" flat className="p-lg lg:bg-surface-container-lowest lg:shadow-card">
        <p className="text-body text-on-primary-fixed">Loading the calendar…</p>
      </Card>
    );
  }

  if (tasks.length === 0) {
    return (
      <Card
        tone="accent"
        flat
        className="flex flex-col gap-1 p-lg lg:bg-surface-container-lowest lg:p-5.5 lg:shadow-card"
      >
        <h2 className="text-section-header text-on-primary-fixed lg:text-section-header-wide">
          Nothing needs you
        </h2>
        <p className="text-body text-on-surface-body">
          Hatch and ringing days appear here the morning they arrive.
        </p>
      </Card>
    );
  }

  const hiddenNarrow = tasks.length - VISIBLE_TASKS_NARROW;

  return (
    /*
     * A sage panel on a phone; a card from `lg`.
     *
     * At 390px a filled card is the screen's one loud object. At 1440px the same fill is a
     * 900px block of sage that outshouts the thing it contains — including the terracotta
     * on an overdue ringing, which is the one colour on this screen that must win. So on a
     * monitor the sage shrinks to the count disc and the row buttons.
     */
    <Card
      tone="accent"
      flat
      className="flex flex-col gap-3 p-lg lg:gap-3.5 lg:bg-surface-container-lowest lg:p-5.5 lg:shadow-card"
    >
      {/* The phone's heading: the words, and a terracotta tag when something is late. */}
      <div className="flex items-center justify-between gap-md lg:hidden">
        <h2 className="text-section-header text-on-primary-fixed">Needs you today</h2>
        {late > 0 ? (
          <StatusPill tone="alarm" size="lg">
            {late} late
          </StatusPill>
        ) : null}
      </div>

      {/* The desktop's: the count as a disc, the heading beside it with what is late said
          in words, and the calendar on the right. */}
      <div className="hidden items-center gap-3.5 lg:flex">
        <span
          aria-hidden
          className={cn(
            "flex size-12.5 shrink-0 items-center justify-center rounded-full",
            "bg-primary-fixed-dim text-disc-wide text-on-primary-fixed",
          )}
          data-numeric
        >
          {total}
        </span>

        <div className="flex min-w-0 flex-1 flex-col">
          <h2 className="text-section-header-wide text-on-surface">Needs you today</h2>
          <span className="text-detail text-on-surface-variant">
            {late === 0
              ? "Nothing is past its window yet"
              : late === total
                ? `${late === 1 ? "It is" : "All of them are"} past ${late === 1 ? "its" : "their"} window`
                : `${late === 1 ? "One" : late} of them ${late === 1 ? "is" : "are"} past ${late === 1 ? "its" : "their"} window`}
          </span>
        </div>

        <Link to="/breeding/calendar" className="shrink-0 text-subtitle font-bold text-on-primary-fixed-variant">
          Open calendar →
        </Link>
      </div>

      {/*
        * Three rows on a phone and five on a desktop, decided in CSS rather than in JS.
        *
        * Rendering all five and hiding two keeps one list and one "more" link; branching on
        * a breakpoint in JavaScript would give the two layouts different truths about how
        * many are hidden, and get them wrong on every resize before a re-render.
        */}
      <ul className="flex flex-col gap-3 lg:gap-3.5">
        {tasks.slice(0, VISIBLE_TASKS_WIDE).map((task, index) => (
          <li
            key={task.key}
            className={index >= VISIBLE_TASKS_NARROW ? "hidden lg:block" : undefined}
          >
            <TaskRow task={task} />
          </li>
        ))}
      </ul>

      {hiddenNarrow > 0 ? (
        <Link
          to="/breeding/calendar"
          className="text-subtitle font-bold text-on-primary-fixed-variant lg:hidden"
        >
          {hiddenNarrow} more on the calendar →
        </Link>
      ) : null}
    </Card>
  );
}

function TaskRow({ task }: { task: GroupedTask }) {
  const { entry, count } = task;
  const ringing = entry.kind === "Ring";
  const late = entry.isOverdue;

  return (
    <Link
      to={`/breeding/nests/${entry.nestId}`}
      className={cn(
        "flex items-center gap-3 rounded-inset p-3 transition-colors",
        "lg:gap-3.5 lg:rounded-card lg:py-3 lg:pl-3.5 lg:pr-3",
        /*
         * Card-white on the phone's sage panel; on the desktop's white card it has to be
         * the other way round or the row and the card are the same colour and the list
         * dissolves. An overdue row takes the warm ground there — at 1440px the ground is
         * what carries across the width of the card.
         */
        late
          ? "bg-surface-container-lowest lg:bg-secondary-faint"
          : "bg-surface-container-lowest lg:bg-surface lg:hover:bg-surface-container-low",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-touch shrink-0 items-center justify-center rounded-full lg:size-10.5",
          late
            ? "bg-secondary-fixed text-on-secondary-fixed-variant lg:bg-action lg:text-on-action"
            : "bg-primary-fixed text-on-primary-fixed-variant",
        )}
      >
        {ringing ? (
          <Baby className="size-5 lg:size-4.75" />
        ) : (
          <EggIcon className="size-5 lg:size-4.75" />
        )}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5 lg:gap-px">
        <span className="truncate text-row text-on-surface lg:text-label" data-numeric>
          {ringing ? (count > 1 ? `Ring ${count} chicks` : "Ring the chick") : "Hatch due"}
        </span>

        <span
          className={cn(
            "text-caption lg:truncate",
            late ? "text-secondary" : "text-on-surface-variant",
          )}
          data-numeric
        >
          {entry.locationCode ? `Cage ${entry.locationCode} · ` : ""}round {entry.round}
          {late
            ? ` · ${Math.abs(entry.daysAway)} ${Math.abs(entry.daysAway) === 1 ? "day" : "days"} late`
            : ` · ${entry.eggCount} eggs`}
        </span>
      </span>

      {/* Terracotta when overdue: an overdue ringing cannot be recovered — past a few
          days the leg has grown and the bird stays unringed for life.

          On a monitor the arrow becomes the action it stands for. The row is still the
          link; the pill is what the eye lands on when five rows are open at once. */}
      <span
        aria-hidden
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-full lg:hidden",
          late ? "bg-action text-on-action" : "bg-surface-container-low text-on-surface-body",
        )}
      >
        <ArrowRight className="size-4.25" />
      </span>

      <span
        aria-hidden
        className={cn(
          "hidden h-10 shrink-0 items-center rounded-control px-lg text-pill lg:inline-flex",
          late ? "bg-action text-on-action" : "bg-primary-fixed text-on-primary-fixed",
        )}
      >
        {ringing ? (count > 1 ? `Ring all ${count}` : "Ring the chick") : "Log hatch"}
      </span>
    </Link>
  );
}

/**
 * The live clutches as dials.
 *
 * 150px cards in a row that swipes: two and a bit show on a phone, and the bit is what
 * says there are more.
 */
function NestsRunning({
  nests,
  pending,
  today,
}: {
  nests: readonly NestSummary[];
  pending: boolean;
  today: string;
}) {
  return (
    <section className="flex flex-col gap-3 lg:gap-3.5">
      <div className="flex items-baseline justify-between gap-sm">
        <h2 className="text-section-header text-on-surface lg:text-section-header-wide">
          Nests running
        </h2>
        <Link to="/breeding" className="text-kicker uppercase text-primary-strong" data-numeric>
          View all<span className="hidden lg:inline"> {nests.length}</span>
        </Link>
      </div>

      {pending ? (
        <p className="text-body text-on-surface-variant">Loading nests…</p>
      ) : nests.length === 0 ? (
        <Card className="p-lg">
          <p className="text-body text-on-surface-variant">
            No round is running. Start one from a pair to see it here.
          </p>
        </Card>
      ) : (
        /* Two share the row at rest, and the rest are a swipe away — an aviary in
           full swing runs more than two rounds, and truncating to the first two hid
           the others behind a "view all" nobody presses. */
        <ScrollRow
          aria-label="Rounds running"
          // A swipe is the right gesture on a phone and the wrong one on a desktop, where
          // there is room to lay the same cards out and nothing to swipe with. Three and
          // four rather than four and five: this sits in the main column now, not across
          // the whole page, and the old counts left an 84px dial adrift in a 265px cell.
          className="gap-2.5 lg:grid lg:grid-cols-4 lg:gap-3.5 lg:overflow-x-visible"
        >
          {nests.map((nest) => (
            <li key={nest.id} className="w-37.5 shrink-0 lg:w-auto">
              <NestDial nest={nest} today={today} />
            </li>
          ))}
        </ScrollRow>
      )}
    </section>
  );
}

/**
 * One running clutch.
 *
 * Stacked and centred on a phone, where the card is 168px wide and a row would put a
 * 64px dial next to eight characters of cage code. From `lg` it turns: the dial takes
 * the left, the identity reads beside it, and the two figures a breeder actually wants —
 * how many eggs, and when they are due — get their own strip along the bottom.
 */
function NestDial({ nest, today }: { nest: NestSummary; today: string }) {
  const progress = incubationProgress(nest.firstEggOn, nest.expectedHatchOn, today);

  return (
    <Link to={`/breeding/nests/${nest.id}`} className="block h-full">
      <Card
        shape="row"
        className={cn(
          "flex h-full flex-col items-center gap-2 px-2.5 py-3.5",
          "lg:items-stretch lg:gap-3 lg:p-4",
          "transition-colors hover:bg-surface-container-low",
        )}
      >
        <div className="flex flex-col items-center gap-2 lg:flex-row lg:items-center lg:gap-3">
          <RadialProgress
            value={progress?.progress ?? 0}
            label={`Round ${nest.round} progress`}
            strokeWidth={12.5}
            className="size-20 lg:size-16 lg:shrink-0"
          >
            <span className="text-dial text-on-surface lg:text-dial-wide" data-numeric>
              {progress ? Math.max(0, progress.day) : "—"}
            </span>
            {progress ? (
              <span className="text-dial-label uppercase text-on-surface-variant lg:hidden" data-numeric>
                of {progress.total}
              </span>
            ) : null}
          </RadialProgress>

          <div className="flex min-w-0 flex-col items-center gap-2 lg:items-start lg:gap-1">
            <span className="truncate text-label text-on-surface" data-numeric>
              {nest.locationCode ?? `Round ${nest.round}`}
            </span>

            <StatusPill tone="positive" className="lg:hidden">
              {nest.status}
            </StatusPill>
            <StatusPill size="sm" tone="positive" className="hidden lg:inline-flex">
              {nest.status}
            </StatusPill>
          </div>
        </div>

        {/* The strip the phone has no room for. */}
        <div
          className="hidden items-center justify-between gap-sm rounded-sm bg-surface-container px-3 py-2 lg:flex"
          data-numeric
        >
          <span className="text-subtitle text-on-surface-body">
            {nest.chickCount > 0
              ? `${nest.chickCount} ${nest.chickCount === 1 ? "chick" : "chicks"}`
              : `${nest.eggCount} ${nest.eggCount === 1 ? "egg" : "eggs"}`}
          </span>
          <span className="text-subtitle font-bold text-on-surface">
            {nest.expectedHatchOn
              ? nest.expectedHatchOn <= today
                ? "Today"
                : `Hatch ${shortDate(nest.expectedHatchOn)}`
              : "—"}
          </span>
        </div>
      </Card>
    </Link>
  );
}

/**
 * One line of work, which may stand for several calendar entries.
 *
 * The calendar answers per chick, because a ringing date is a property of a chick and
 * not of a nest — three chicks that hatched on three days have three windows. That is
 * the right answer for a calendar and the wrong one for this list: five rows reading
 * "Ring the chicks · cage BR-I03 · round 3" differ only in a chick id the breeder cannot
 * see, and a list that repeats itself five times is one they stop reading at the second.
 *
 * So the rows collapse per nest and kind, and the count becomes a number on the row.
 * What a breeder does next is one trip to one cage either way.
 */
interface GroupedTask {
  key: string;
  /** The most overdue of the group — it decides the row's tone and its detail line. */
  entry: CalendarEntry;
  count: number;
}

function groupTasks(entries: readonly CalendarEntry[]): readonly GroupedTask[] {
  const groups = new Map<string, GroupedTask>();

  for (const entry of entries) {
    const key = `${entry.nestId}:${entry.kind}`;
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, { key, entry, count: 1 });
      continue;
    }

    existing.count += 1;

    // Least `daysAway` is furthest past its window, and that is the one whose lateness
    // the collapsed row has to report.
    if (entry.daysAway < existing.entry.daysAway) {
      existing.entry = entry;
    }
  }

  return [...groups.values()].sort((a, b) => a.entry.daysAway - b.entry.daysAway);
}

/**
 * Search, from the screen a breeder lands on.
 *
 * It goes to the register rather than answering inline: every one of "ring number, cage,
 * mutation" is a bird, the register already searches on all three, and a second set of
 * results on Today would be a second place to learn.
 */
function TodaySearch() {
  const [term, setTerm] = useState("");
  const navigate = useNavigate();

  return (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();

        if (term.trim()) {
          void navigate(`/birds?search=${encodeURIComponent(term.trim())}`);
        }
      }}
      className="relative hidden w-75 shrink-0 md:block"
    >
      <Search
        className="pointer-events-none absolute left-lg top-1/2 size-4.5 -translate-y-1/2 text-on-surface-variant"
        aria-hidden
      />
      <input
        type="search"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder="Ring number, cage, mutation"
        aria-label="Search the register"
        className={cn(
          "h-12 w-full rounded-control bg-surface-container-lowest shadow-card",
          "pl-[calc(var(--spacing-lg)+var(--spacing)*7)] pr-lg",
          "text-body text-on-surface placeholder:text-on-surface-variant",
          "outline-none transition-shadow",
          "focus:shadow-[inset_0_0_0_2.75px_var(--color-action)]",
        )}
      />
    </form>
  );
}

interface CageRollup {
  code: string;
  nests: number;
  late: number;
}

/**
 * The cages that have something in them, with how much is late in each.
 *
 * Built from the nests and the calendar rather than from `/locations`, and that is the
 * point: the rail lists the cages a breeder has work in this morning, not every cage
 * they own. An aviary with forty cages and three rounds running should show three rows.
 *
 * Sorted by lateness first — the rail is read top-down, and the cage with three overdue
 * chicks in it is the one worth putting under the eye.
 */
function useCageRollup(
  nests: readonly NestSummary[],
  entries: readonly CalendarEntry[],
): readonly CageRollup[] {
  return useMemo(() => {
    const byCage = new Map<string, CageRollup>();

    for (const nest of nests) {
      if (!nest.locationCode) {
        continue;
      }

      const cage = byCage.get(nest.locationCode) ?? { code: nest.locationCode, nests: 0, late: 0 };
      cage.nests += 1;
      byCage.set(cage.code, cage);
    }

    for (const entry of entries) {
      if (!entry.locationCode || !entry.isOverdue) {
        continue;
      }

      const cage = byCage.get(entry.locationCode) ?? {
        code: entry.locationCode,
        nests: 0,
        late: 0,
      };
      cage.late += 1;
      byCage.set(cage.code, cage);
    }

    return [...byCage.values()].sort(
      (a, b) => b.late - a.late || a.code.localeCompare(b.code, undefined, { numeric: true }),
    );
  }, [nests, entries]);
}

/**
 * The live withdrawal, on the desktop aside.
 *
 * Warm all over rather than a warm rule, because what it says is a prohibition: these
 * birds cannot be shown or sold until the date on it. The bar is days elapsed of the
 * course, not of the withdrawal — the course is the thing still being done.
 */
function WithdrawalCard({ record, today }: { record: HealthRecordDto; today: string }) {
  const until = record.withdrawalEndsOn;
  const progress = courseProgress(record, today);
  const where = record.locationCode ?? record.birdRingNumber;
  const birds =
    record.subjectCount > 1 ? `${record.subjectCount} birds` : record.subjectCount === 1 ? "1 bird" : "Birds";

  return (
    <Link
      to="/health"
      className="hidden flex-col gap-2.5 rounded-lg bg-secondary-fixed p-lg text-on-secondary-fixed lg:flex"
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className="flex size-9.5 shrink-0 items-center justify-center rounded-full bg-secondary-strong text-on-action"
        >
          <Syringe className="size-4.25" />
        </span>
        <span className="min-w-0 flex-1 truncate text-panel-title">Withdrawal running</span>
      </div>

      <span className="text-detail-lg" data-numeric>
        {record.title}
        {where ? ` in ${where}` : ""} · {birds} can&rsquo;t be shown or sold
        {until ? ` until ${shortDate(until)}` : ""}.
      </span>

      {/* How far through the course, which is what the card is counting. The track is
          the same ink at low opacity so the bar reads as one object, not two. */}
      {progress === null ? null : (
        <div className="flex h-2.5 overflow-hidden rounded-full bg-secondary-strong/18">
          <div
            className="rounded-full bg-secondary-strong transition-[width] duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </Link>
  );
}

/** How far through the course, 0–100, or null when it has no end to measure against. */
function courseProgress(record: HealthRecordDto, today: string): number | null {
  if (!record.startsOn || !record.endsOn) {
    return null;
  }

  const total = daysBetween(record.startsOn, record.endsOn) + 1;
  const done = daysBetween(record.startsOn, today) + 1;

  return total <= 0 ? null : Math.max(0, Math.min(100, Math.round((done / total) * 100)));
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}

function shortDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);

  if (!year || !month || !day) {
    return iso;
  }

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function greeting(): string {
  const hour = new Date().getHours();

  return hour < 12 ? "Morning" : hour < 18 ? "Afternoon" : "Evening";
}

function longDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);

  if (!year || !month || !day) {
    return "";
  }

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

function firstName(displayName?: string | null): string {
  return (displayName ?? "").trim().split(/\s+/)[0] || "breeder";
}

function format(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toString();
}
